import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  getWhoWeServeAdmin,
  updateWhoWeServeItem,
  deleteWhoWeServeItem,
} from "@/lib/api/marketing.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { MarketingAdminNav } from "@/components/admin/MarketingAdminNav";
import { ServiceItemEditor } from "@/components/admin/ServiceItemEditor";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { imageFromMarketing, type MediaSelection } from "@/lib/media/types";

export const Route = createFileRoute("/admin/marketing/who-we-serve/$id")({
  loader: ({ params }) => getWhoWeServeAdmin({ data: { id: params.id } }),
  component: EditWhoWeServePage,
});

function EditWhoWeServePage() {
  const item = Route.useLoaderData();
  const navigate = useNavigate();
  if (!item) throw notFound();

  const [name, setName] = useState(item.name);
  const [body, setBody] = useState(item.body ?? "");
  const [image, setImage] = useState<MediaSelection>(imageFromMarketing(item));
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateWhoWeServeItem({
        data: {
          id: item.id,
          name,
          body,
          imageMediaId: image.mediaId,
          imageUrl: image.url,
          status,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete "${item.name}"?`)) return;
    await deleteWhoWeServeItem({ data: { id: item.id } });
    toast.success("Deleted");
    navigate({ to: "/admin/marketing/who-we-serve" });
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Edit audience"
        action={
          <Button variant="destructive" onClick={() => void handleDelete()}>
            Delete
          </Button>
        }
      />
      <MarketingAdminNav />
      <div className="space-y-4">
        <ServiceItemEditor
          name={name}
          description={body}
          image={image}
          onNameChange={setName}
          onDescriptionChange={setBody}
          onImageChange={setImage}
        />
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <AdminFormFooter cancelTo="/admin/marketing/who-we-serve" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
